import PropTypes from "prop-types";
import { RadioButton } from "primereact/radiobutton";

import Label from "../Label";

import "./InputRadio.css";

/**
 * A group of radio buttons with an optional label, required indicator, and error message.
 *
 * @param {object} props
 * @param {string} props.label - Text shown as the group legend.
 * @param {string} props.name - HTML name shared by all radio inputs in the group; also used to derive accessible IDs.
 * @param {*} [props.checkedValue] - The currently selected value; a radio is checked when its value matches this.
 * @param {function} [props.onChange] - Called with the PrimeReact change event when an option is selected.
 * @param {string} [props.error] - Error message displayed below the group.
 * @param {boolean} [props.inline=true] - Layout direction: `true` → row, `false` → column.
 * @param {boolean} [props.isRequired=false] - When true, renders a required indicator on the label and sets `aria-required` on the group.
 * @param {Array<{
 *   id:    string,
 *   value: *,
 *   label: string,
 *   description?: string,
 * }>} [props.radios=[]] - Options to render.
 */
const InputRadio = ({
  label,
  name,
  isRequired,
  checkedValue,
  onChange,
  error,
  inline = true,
  disabled = false,
  radios = [],
}) => {
  const labelId = `${name}-group-label`;
  const errorId = `${name}-group-error`;

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      aria-required={isRequired || undefined}
      aria-describedby={error ? errorId : undefined}
      className="flex flex-col gap-1"
    >
      {!!label && (
        <div id={labelId}>
          <Label text={label} isRequired={!!isRequired} />
        </div>
      )}
      <div className={`flex ${inline ? "flex-row gap-3" : "flex-col gap-2"}`}>
        {radios.map((radio) => (
          <div key={radio.id} className="flex flex-col gap-1">
            <div className="flex align-middle">
              <RadioButton
                inputId={radio.id}
                name={name}
                value={radio.value}
                onChange={onChange}
                checked={checkedValue === radio.value}
                disabled={disabled}
              />
              <label
                htmlFor={radio.id}
                className={`ml-1 ${label ? "font-normal" : "font-bold"}`}
              >
                {radio.label}
              </label>
            </div>
            {radio.description && (
              <p className="text-sm text-secondaryText ml-4">
                {radio.description}
              </p>
            )}
          </div>
        ))}
      </div>
      {!!error && (
        <small id={errorId} role="alert" className="text-red-500 text-xs">
          {error}
        </small>
      )}
    </div>
  );
};

InputRadio.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  checkedValue: PropTypes.any,
  onChange: PropTypes.func,
  error: PropTypes.string,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  radios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
};

export default InputRadio;
